import { useCallback, useEffect, useMemo, useState } from 'react';
import { TABLE_MIN_HEIGHT } from 'framework-listing-lib/constants/sizes';
import { REQUEST_TABLE_RESIZE } from '../../../constants/tableEvents';
import listingLibObserver from '../../../utils/listingLibObserver';
const useOnRequestTableResize = cb => {
  useEffect(() => {
    listingLibObserver.on(REQUEST_TABLE_RESIZE, () => {
      requestAnimationFrame(cb);
    });
  }, [cb]);
};
const tryGetPageHasPageBeenScrolled = () => {
  var _document$querySelect, _document$querySelect2;
  const scrolledDistance = ((_document$querySelect = document.querySelector('.page')) === null || _document$querySelect === void 0 ? void 0 : _document$querySelect.scrollTop) || ((_document$querySelect2 = document.querySelector('.copilot-app-container')) === null || _document$querySelect2 === void 0 ? void 0 : _document$querySelect2.scrollTop) || window.scrollY;
  return scrolledDistance !== 0;
};
function parseInitialValue(initial) {
  if (initial === 'auto' || initial === 'full-height') {
    return initial;
  }
  return `${initial}px`;
}

/**
 * Calculates the maximum height @elementRef can take on screen.
 * It is calculated based on the offset top of @elementRef minus whichever @heightAffordances it might need.
 */
export default function useCalculateMaxHeight({
  elementRef,
  initial,
  heightAffordances
}) {
  const [height, setHeight] = useState(parseInitialValue(initial));
  const shouldCalculate = useMemo(() => initial === 'full-height', [initial]);
  const calculate = useCallback(() => {
    if (!elementRef) return;
    const {
      top
    } = elementRef.getBoundingClientRect();
    const newHeight = `max(calc(100vh - ${top + heightAffordances}px), ${TABLE_MIN_HEIGHT}px)`;
    setHeight(prevHeight => {
      if (prevHeight === newHeight) {
        return prevHeight;
      }
      return newHeight;
    });
  }, [elementRef, heightAffordances]);
  useOnRequestTableResize(calculate);

  /**
   * Recalculate table max-height by ignoring paginator's height and page padding bottom.
   */
  useEffect(() => {
    if (!shouldCalculate) {
      const newInitial = parseInitialValue(initial);
      setHeight(prevHeight => prevHeight !== newInitial ? newInitial : prevHeight);
      return undefined;
    }
    const offsetTopObserver = new MutationObserver(() => {
      /**
       * Stop calculating the table height when the page has been scrolled.
       */
      if (tryGetPageHasPageBeenScrolled()) {
        return;
      }
      calculate();
    });
    if (elementRef) {
      offsetTopObserver.observe(elementRef, {
        attributes: true,
        childList: true,
        subtree: true
      });
      calculate();
    }
    return () => offsetTopObserver.disconnect();
  }, [elementRef, heightAffordances, initial, shouldCalculate, calculate]);
  return height;
}